<script setup lang="ts">
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import type { EquipItemType, EquipSellRes } from '@/types/equip'
import {
  equipQualityText,
  getReinforceAttr,
  getRiseStarAttr,
  supremeLevelQualityMap,
  supremeAffixLevelQualityMap,
  noPercentEquipAttrKeyList,
} from '../data'
import { BasicAttrTextEnum, EquipPriceEnum } from '@/enums'
import { equipCollection } from '@/core/equip'
import { ElMessageBox } from 'element-plus'
import type { UserAttrType } from '@/types/user'
import { socketEmitAsync } from '@/utils/web-socket'
import { useUserStore } from '@/stores/modules/user'
import type { EquipActionRes, ItemType } from '@/types/item'
import { containsArray, formatNumber } from '@/utils'
import { explainEquipAffix } from '@/core/equip/affix'
import { Lock, Unlock } from '@element-plus/icons-vue'
import { getEquipName } from '@/utils/equip'
import { explainDarkGoldEquipAffix, getDarkGoldBasicAttrValue } from '@/core/equip/dark-gold'
import { supremeAffixCollection } from '@/core/equip/supreme'
import { getSkillName, getSkillNameById } from '@/core/skill'
import { getSupremeAffixBasicAttrValue, getSupremeRealAffixList } from '@/core/equip/supreme'

const userStore = useUserStore()
const props = withDefaults(
  defineProps<{
    equipInfo: EquipItemType
    isInItems?: boolean
    isInComparison?: boolean
    showAction?: boolean
    showLock?: boolean
    showExtra?: boolean
  }>(),
  {
    isInItems: true,
    isInComparison: false,
    showAction: true,
    showLock: true,
    showExtra: true,
  },
)
const emits = defineEmits(['clear', 'lock', 'compare', 'closeCompare', 'sell'])

function getBasicAttrValue(key: keyof UserAttrType) {
  if (!props.equipInfo) return 0
  if (key === 'skillGrow') return 0
  const { equipId, quality } = props.equipInfo

  const equipInfo = equipCollection[equipId]
  const basicVal = equipInfo?.attrs.basic[key] || 0
  const mag = quality === 'race' ? 1.1 : quality === 'epic' ? 1.3 : 1

  if (quality === 'supreme') return basicVal
  return Math.ceil(basicVal * mag)
}

async function handleLock() {
  const id = props.equipInfo.id
  await socketEmitAsync<ItemType[]>({ type: 'lockEquip', data: { id } })
  const item = userStore.itemList.find((item) => item.id === id)
  if (!item) return
  item.isLock = !item.isLock
  emits('lock')
}
async function handleEquip(action: 'wear' | 'remove') {
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  const id = props.equipInfo.id
  const res = await socketEmitAsync<EquipActionRes>({ type: 'equipAction', data: { id, action } })
  userStore.equippedList = res.equippedList
  userStore.userAttr = res.userAttr
  userStore.setRoutineEquippedList()
  emits('clear')
  if (props.isInComparison) emits('closeCompare')
}
function handleSell() {
  const { equipInfo } = props
  const { id, quality = 'normal', reinforcedLevel, starAttrs } = equipInfo
  if (equipInfo.isLock) return ElMessage.warning('装备已锁定，无法出售')
  if (['unique', 'legend', 'antiquity', 'peerless', 'darkGold', 'myth', 'supreme'].includes(quality) || reinforcedLevel > 0 || starAttrs?.length) {
    ElMessageBox.confirm(`<p class="affix">确定出售<span class="${quality}">${getEquipName(equipInfo, equipInfo.type)}</span>吗？</p>`, '提示', {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    })
      .then(() => sell())
      .catch(() => {})
  } else {
    sell()
  }

  async function sell() {
    const { itemList, gold } = await socketEmitAsync<EquipSellRes>({ type: 'sellEquip', data: { idList: [id] } })
    userStore.itemList = itemList
    userStore.gold = gold
    emits('sell')
  }
}

function isSuitEquippedFull(equipIdList: number[]) {
  return containsArray(userStore.getEquippedEquipIdList, equipIdList) //
}
function getRefineStarClass(index: number) {
  const { refineAttr } = props.equipInfo
  if (!refineAttr) return ''
  const count = getRefineHitCount(index)
  if (count === 1) return 'race'
  else if (count === 2) return 'epic'
  else if (count === 3) return 'legend'
  else if (count === 4) return 'antiquity'
}
function getRefineHitCount(index: number) {
  const { refineAttr } = props.equipInfo
  if (!refineAttr) return 0
  return refineAttr[1].reduce((pre, item) => (pre += item === index ? 1 : 0), 0)
}
</script>

<template>
  <div class="equip-info affix" v-bind="$attrs">
    <p :class="`${equipInfo?.type}`" style="position: relative; margin-bottom: 8px; font-weight: bold">
      <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
      <span class="equip-lock" v-show="showAction && showLock">
        <el-button size="small" :type="equipInfo.isLock ? 'success' : 'info'" plain @click="handleLock">
          <el-icon size="20" v-show="!equipInfo.isLock"><Unlock /></el-icon>
          <el-icon size="20" v-show="equipInfo.isLock"><Lock /></el-icon>
        </el-button>
      </span>
    </p>
    <p>
      品质：<span :class="equipInfo.quality">{{ equipQualityText[equipInfo?.quality || 'normal'] }}</span>
    </p>
    <p v-if="equipInfo.supremeAttrs">
      阶级：
      <span :class="supremeLevelQualityMap[equipInfo.supremeAttrs.level].classes">
        {{ supremeLevelQualityMap[equipInfo.supremeAttrs.level].text }}
      </span>
    </p>
    <p>
      售价：<span class="gold">{{ EquipPriceEnum[equipInfo.quality] }}</span>
    </p>
    <p>属性：</p>
    <p v-for="(val, key) of equipInfo.attrs?.basic" :key="key">
      {{ BasicAttrTextEnum[key] }}
      <template v-if="key !== 'skillGrow'">
        <span v-if="val && (val as number) > 0">
          + <span class="grow">{{ getBasicAttrValue(key) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
        </span>
        <span v-else>
          -
          <span class="shrink">{{ Math.abs(getBasicAttrValue(key)) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
        </span>
      </template>
    </p>
    <p v-if="equipInfo.quality === 'supreme'">抗魔值 + <span class="grow">30</span></p>
    <template v-if="equipInfo.darkGoldAttrs">
      <br />
      <p>暗金属性：</p>
      <p v-for="([key, val], index) of equipInfo.darkGoldAttrs.basic" :key="key">
        <span v-if="val === 10" :class="getRefineStarClass(index)">★ </span>
        <span :class="getRefineStarClass(index)">{{ BasicAttrTextEnum[key] }}</span>
        <span v-if="val && val > 0">
          +
          <span class="grow"
            >{{ getDarkGoldBasicAttrValue(key, val + 5, getRefineHitCount(index))
            }}{{ ['crt', 'crtd', 'hr', 'atksp', 'ad'].includes(key) ? '%' : '' }}</span
          >
        </span>
      </p>
      <template v-if="equipInfo.darkGoldAttrs.special">
        <p v-for="item of explainDarkGoldEquipAffix(equipInfo.darkGoldAttrs.special, props.showExtra)" v-html="item"></p>
      </template>
    </template>
    <template v-if="equipInfo.supremeAttrs">
      <br />
      <p>至臻属性：</p>
      <div
        v-for="(affixKey, index) of getSupremeRealAffixList(equipInfo.supremeAttrs, equipInfo.supremeEngraveAttr)"
        :style="index === 0 ? '' : 'margin: 10px 0'"
      >
        <p :class="supremeAffixLevelQualityMap[supremeAffixCollection[affixKey].level]">
          <span v-if="equipInfo.supremeEngraveAttr?.affixIndex === index" class="info">↺</span>
          <span v-if="(equipInfo.refineAttr?.[1] || []).includes(index)" :class="getRefineStarClass(index)">★ </span>
          {{ supremeAffixCollection[affixKey].name }}：
        </p>
        <p v-for="(val, key) of supremeAffixCollection[affixKey].attrs">
          <template v-if="key !== 'skillGrow'">
            <span :class="getRefineStarClass(index)">{{ BasicAttrTextEnum[key] }}</span>
            <span v-if="val && (val as number) > 0">
              +
              <span class="grow"
                >{{ getSupremeAffixBasicAttrValue(key, val as number, getRefineHitCount(index))
                }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span
              >
            </span>
            <span v-else>
              -
              <span class="shrink"
                >{{ getSupremeAffixBasicAttrValue(key, Math.abs((val as number)!), getRefineHitCount(index))
                }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span
              >
            </span>
          </template>
          <template v-else>
            <p v-for="(levelAdd, skillId) in val">
              <span :class="getRefineStarClass(index)">{{ getSkillNameById(Number(skillId), true) }}</span>
              <span
                >+ <span class="grow">{{ getSupremeAffixBasicAttrValue('atk', levelAdd, getRefineHitCount(index)) }}</span></span
              >
            </p>
          </template>
        </p>
      </div>
    </template>
    <template v-if="equipInfo.engraveAttr">
      <br />
      <p>刻印属性：</p>
      <template v-if="equipInfo.engraveAttr.special">
        <p v-for="item of explainDarkGoldEquipAffix(equipInfo.engraveAttr.special, props.showExtra)" v-html="item"></p>
      </template>
    </template>

    <template v-if="equipInfo.reinforcedLevel">
      <br />
      <p>强化属性：</p>
      <p v-for="(val, key) of getReinforceAttr(equipInfo.type, equipInfo.reinforcedLevel)">
        {{ BasicAttrTextEnum[key] }}
        + <span class="grow">{{ val }}{{ ['crt', 'crtd', 'hr', 'atksp', 'ad'].includes(key) ? '%' : '' }}</span>
      </p>
    </template>
    <template v-if="equipInfo.starAttrs">
      <br />
      <p>升星属性：</p>
      <p v-for="item of getRiseStarAttr(equipInfo.starAttrs)">
        {{ BasicAttrTextEnum[Object.keys(item)[0] as keyof UserAttrType] }}
        <span>
          +
          <span class="grow">
            {{ Object.values(item)[0] }}{{ ['crt', 'crtd', 'hr', 'atksp'].includes(Object.keys(item)[0] as keyof UserAttrType) ? '%' : '' }}
          </span>
        </span>
      </p>
    </template>
    <template v-if="equipInfo.enchantAttr">
      <br />
      <p>附魔属性：</p>
      <p>
        抗魔值 + <span class="grow">{{ equipInfo.enchantAttr[0] * 10 }}</span>
      </p>
      <p>
        全伤害加成 + <span class="grow">{{ formatNumber(equipInfo.enchantAttr[1], 2) }}%</span>
      </p>
    </template>
    <template v-if="equipInfo.refineAttr">
      <br />
      <p>精造属性：</p>
      <p>
        {{ BasicAttrTextEnum.ad }}
        <span>
          +
          <span class="grow">{{ formatNumber(equipInfo.refineAttr[0] * (equipInfo.quality === 'darkGold' ? 0.4 : 0.6), 1) }}%</span>
        </span>
      </p>
      <p v-if="equipInfo.quality === 'supreme'">
        {{ BasicAttrTextEnum.voidDef }}
        <span>
          +
          <span class="grow">{{ equipInfo.refineAttr[0] }}</span>
        </span>
      </p>
    </template>
    <template v-if="equipInfo.attrs.special">
      <br />
      <p>特殊属性：</p>
      <p v-for="item of explainEquipAffix(equipInfo.attrs.special, props.showExtra)" v-html="item"></p>
    </template>
    <template v-if="equipInfo.attrs.suit">
      <br />
      <p>套装属性：</p>
      <p v-for="item of equipInfo.attrs.suit.equipIdList" :class="userStore.getEquippedEquipIdList.includes(item) ? 'equipped' : 'un-equipped'">
        {{ equipCollection[item].name }}
      </p>
      <p>
        <span class="unique">{{ equipInfo.attrs.suit.equipIdList.length }} </span>
        件套
        <span :class="isSuitEquippedFull(equipInfo.attrs.suit.equipIdList) ? 'equipped' : 'un-equipped'">
          （{{ equipInfo.attrs.suit.name }}）：
        </span>
      </p>
      <p v-for="(val, key) of equipInfo.attrs.suit.attrs.basic">
        {{ BasicAttrTextEnum[key] }}
        <template v-if="key !== 'skillGrow'">
          <span v-if="val && (val as number) > 0">
            + <span class="grow">{{ val }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
          </span>
          <span v-else>
            -
            <span class="shrink">{{ Math.abs((val as number)!) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
          </span>
        </template>
      </p>
      <template v-if="equipInfo.attrs.suit.attrs?.affix">
        <p v-for="item of explainEquipAffix(equipInfo.attrs.suit.attrs?.affix, props.showExtra)" v-html="item"></p>
      </template>
    </template>
  </div>
  <el-row class="equip-action" v-show="isInItems && showAction">
    <el-col :span="8">
      <CommonBtn boardKey="1" showKey="Alt+1" withAlt type="success" size="small" @done="handleEquip('wear')"> 装备 </CommonBtn>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <CommonBtn boardKey="2" showKey="Alt+2" withAlt type="primary" size="small" @done="emits('compare')">比对</CommonBtn>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <CommonBtn boardKey="3" showKey="Alt+3" withAlt type="warning" size="small" @done="handleSell">出售</CommonBtn>
    </el-col>
  </el-row>
  <el-row class="equip-action" v-show="!isInItems && showAction">
    <el-col :span="8">
      <CommonBtn boardKey="1" showKey="Alt+1" withAlt type="danger" size="small" @done="handleEquip('remove')">脱下</CommonBtn>
    </el-col>
  </el-row>
</template>

<style lang="scss">
.equip-info {
  width: 100%;
  font-size: 14px;
  height: 100%;
  overflow: auto;
  margin-bottom: 10px;
  p {
    margin: 4px 0;
  }
  .equip-lock {
    position: absolute;
    right: 4px;
    top: calc(100% + 10px);
  }
  .refine-wrap {
    display: none;
  }
  .dark-gold-spec-info {
    display: none;
  }
}
</style>
